'use client';

import { c as _c } from "react/compiler-runtime";
import { useHeaderScroll } from '@sprd/spreadshirt-shell';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import Link from 'next/link';
import { useIsAbTestFeatureEnabled } from '@/atoms/abTestConfigAtom';
import { secondLevelOpenAtom } from '@/store/atoms';
import { inPageNavigationTracking } from './inPageNavigationTracking';
type AnchorLink = {
  id: string;
  label: string;
};
type InPageNavigationProps = {
  links: AnchorLink[];
  moduleName: string;
  className?: string;
};
function throttle<T extends unknown[]>(fn: (...args: T) => void, delay: number) {
  let lastCall = 0;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: T) => {
    const now = Date.now();
    const remaining = delay - (now - lastCall);
    if (remaining <= 0) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      lastCall = now;
      fn(...args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        lastCall = Date.now();
        timeout = null;
        fn(...args);
      }, remaining);
    }
  };
}
const getStickyTop = (isFixed: boolean, isHeaderVisible: boolean, isSecondLevelOpen: boolean, isTrustpilotBanner: boolean) => {
  if (!isFixed) {
    return {
      top: 0,
      transform: 'translateY(0)'
    };
  }
  const parts = ['var(--header-banner-height)'];
  if (isHeaderVisible) {
    parts.push('var(--header-height)');
    if (isTrustpilotBanner) {
      parts.push('var(--trustpilot-banner-height)');
    }
  }
  if (isHeaderVisible && isSecondLevelOpen) parts.push('var(--navigation-second-level-height)');
  const translateValue = parts.length > 1 ? `calc(${parts.join(' + ')})` : parts[0];
  return {
    top: 0,
    transform: `translateY(${translateValue})`
  };
};
export default function InPageNavigation(t0) {
  const $ = _c(33);
  const {
    links,
    moduleName,
    className
  } = t0;
  const [activeId, setActiveId] = useState(null);
  const [isFixed, setIsFixed] = useState(false);
  const placeholderRef = useRef(null);
  const lastScrollY = useRef(0);
  const isSecondLevelOpen = useAtomValue(secondLevelOpenAtom);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      expectedState: "variant"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isTrustpilotBanner = useIsAbTestFeatureEnabled("trustpilot-banner", t1);
  const {
    isVisible: isHeaderVisible
  } = useHeaderScroll();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        if (placeholderRef.current) {
          const rect = placeholderRef.current.getBoundingClientRect();
          const fixpoint = currentScrollY > lastScrollY.current ? rect.bottom : rect.top;
          setIsFixed(fixpoint <= 0);
        }
        lastScrollY.current = currentScrollY;
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== links) {
    t3 = [links];
    $[2] = links;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== links) {
    t4 = () => {
      let activeFound;
      const visibleSections = [];
      links.forEach(t5 => {
        const {
          id
        } = t5;
        const el = document.querySelector(`[data-anchor-id="${id}"]`);
        if (!el) {
          return;
        }
        const rect_0 = el.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        if (rect_0.height === 0) {
          return;
        }
        const visibleHeight = Math.min(rect_0.bottom, windowHeight) - Math.max(rect_0.top, 0);
        if (visibleHeight <= 0) {
          return;
        }
        const visiblePercent = visibleHeight / rect_0.height;
        if (visiblePercent >= 0.6) {
          visibleSections.push({
            id,
            visiblePercent,
            top: rect_0.top < -10 ? 0 : rect_0.top
          });
        }
      });
      if (visibleSections.length > 0) {
        visibleSections.sort(_temp);
        activeFound = visibleSections[0]?.id || null;
      } else {
        activeFound = null;
      }
      setActiveId(activeFound);
    };
    $[4] = links;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const detectActiveSection = t4;
  let t5;
  if ($[6] !== detectActiveSection) {
    t5 = throttle(detectActiveSection, 100);
    $[6] = detectActiveSection;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const throttledDetect = useRef(t5);
  let t6;
  let t7;
  if ($[8] !== detectActiveSection) {
    t6 = () => {
      const currentThrottledDetect = throttledDetect.current;
      window.addEventListener("scroll", currentThrottledDetect, {
        passive: true
      });
      window.addEventListener("resize", currentThrottledDetect);
      const onHashChange = () => {
        setTimeout(detectActiveSection, 50);
      };
      window.addEventListener("hashchange", onHashChange);
      detectActiveSection();
      return () => {
        window.removeEventListener("scroll", currentThrottledDetect);
        window.removeEventListener("resize", currentThrottledDetect);
        window.removeEventListener("hashchange", onHashChange);
      };
    };
    t7 = [detectActiveSection];
    $[8] = detectActiveSection;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div ref={placeholderRef} className="h-14" />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = isFixed ? "fixed left-0 z-21 w-full bg-gray-100 duration-150" : "relative mx-auto max-w-screen-xl rounded-sm bg-neutral-100 duration-0 md:w-fit";
  let t10;
  if ($[12] !== className || $[13] !== t9) {
    t10 = clsx(t9, "flex overflow-x-auto px-2 py-2 transition-all no-scrollbar md:justify-start", className);
    $[12] = className;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== isFixed || $[16] !== isHeaderVisible || $[17] !== isSecondLevelOpen || $[18] !== isTrustpilotBanner) {
    t11 = getStickyTop(isFixed, isHeaderVisible, isSecondLevelOpen, isTrustpilotBanner);
    $[15] = isFixed;
    $[16] = isHeaderVisible;
    $[17] = isSecondLevelOpen;
    $[18] = isTrustpilotBanner;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== activeId || $[21] !== links || $[22] !== moduleName) {
    let t13;
    if ($[24] !== activeId || $[25] !== moduleName) {
      t13 = link => <Link key={link.id} href={`#${link.id}`} data-tracking={inPageNavigationTracking(moduleName, link.id)} aria-current={activeId === link.id ? "true" : undefined} className={clsx(activeId === link.id ? "border border-gray-300 bg-white text-black" : "border border-transparent text-black hover:bg-white", "rounded-xs px-3 py-2 font-semibold transition-colors duration-150")}>{link.label}</Link>;
      $[24] = activeId;
      $[25] = moduleName;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    t12 = links.map(t13);
    $[20] = activeId;
    $[21] = links;
    $[22] = moduleName;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <nav className="mx-auto flex min-w-fit space-x-2 overflow-x-auto whitespace-nowrap no-scrollbar md:max-w-screen-xl" role="navigation" aria-label="Section navigation">{t12}</nav>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t13) {
    t14 = <>{t8}<div className={t10} style={t11}>{t13}</div></>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
function _temp(a, b) {
  if (b.visiblePercent !== a.visiblePercent) {
    return b.visiblePercent - a.visiblePercent;
  }
  return a.top - b.top;
}